import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import useDarkMode from "../../../hooks/useDarkMode";
import CustomModal from "../modal/customModal";
import showErrorToast from "../toast/toast";
import {
  getCroppedImageData,
  isFullImageNaturalCrop,
  scalePixelCropToNatural,
} from "./imageCropCanvas";

function buildInitialCrop(mediaWidth, mediaHeight, aspect) {
  if (aspect && aspect > 0) {
    return centerCrop(
      makeAspectCrop(
        { unit: "%", width: 90 },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }
  return {
    unit: "%",
    x: 5,
    y: 5,
    width: 90,
    height: 90,
  };
}

const ImageCropModal = ({
  isOpen,
  imageSrc,
  originalFile,
  aspect = 1,
  onClose,
  onConfirm,
  title = "Crop image",
  subtitle = "Click the image to crop, or select it as-is. A new library copy is saved only when you crop.",
  confirmButtonText = "Select image",
  /** Stack above another modal (e.g. media library). Rendered via portal to document.body. */
  zIndexBase = 13000,
}) => {
  const isDarkMode = useDarkMode();
  const imgRef = useRef(null);
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (isOpen && imageSrc) {
      setCropMode(false);
      setCrop(undefined);
      setCompletedCrop(null);
      setApplying(false);
    }
  }, [isOpen, imageSrc]);

  const onImageLoad = useCallback(
    (e) => {
      if (!cropMode) return;
      const { width, height } = e.currentTarget;
      setCrop(buildInitialCrop(width, height, aspect));
    },
    [aspect, cropMode]
  );

  const enableCropMode = () => {
    if (cropMode) return;
    setCropMode(true);
    const img = imgRef.current;
    if (img && img.width && img.height) {
      setCrop(buildInitialCrop(img.width, img.height, aspect));
    }
  };

  const handleApply = async () => {
    // Select without enabling crop → use original library file (no copy)
    if (!cropMode) {
      onConfirm(null, null, { wasCropped: false });
      return;
    }

    const img = imgRef.current;
    if (!imageSrc || !completedCrop || !img) {
      showErrorToast("Adjust the crop area, then select the image.");
      return;
    }

    const naturalCrop = scalePixelCropToNatural(
      completedCrop,
      img.width,
      img.height,
      img.naturalWidth,
      img.naturalHeight
    );

    if (
      !naturalCrop ||
      naturalCrop.width < 2 ||
      naturalCrop.height < 2
    ) {
      showErrorToast("Selection is too small. Enlarge the crop area.");
      return;
    }

    // Crop covers entire image → still no need for a duplicate file
    if (
      isFullImageNaturalCrop(
        naturalCrop,
        img.naturalWidth,
        img.naturalHeight
      )
    ) {
      onConfirm(null, null, { wasCropped: false });
      return;
    }

    setApplying(true);
    try {
      const mimeType =
        originalFile?.type && originalFile.type.startsWith("image/")
          ? originalFile.type
          : "image/jpeg";
      const { blob, dataUrl } = await getCroppedImageData(
        imageSrc,
        naturalCrop,
        mimeType,
        0.92
      );
      const baseName = originalFile?.name || "image.jpg";
      const nameWithoutExt = baseName.replace(/\.[^.]+$/, "");
      const ext =
        mimeType === "image/png"
          ? ".png"
          : mimeType === "image/webp"
          ? ".webp"
          : ".jpg";
      const file = new File([blob], `${nameWithoutExt}-cropped${ext}`, {
        type: mimeType,
      });
      onConfirm(dataUrl, file, { wasCropped: true });
    } catch {
      showErrorToast("Could not crop image. Try another file.");
    } finally {
      setApplying(false);
    }
  };

  const canApply = cropMode
    ? completedCrop && completedCrop.width >= 4 && completedCrop.height >= 4
    : Boolean(imageSrc);

  if (!isOpen) return null;

  const previewBoxStyle = {
    minHeight: "260px",
    maxHeight: "min(58vh, 440px)",
    overflow: "auto",
    backgroundColor: isDarkMode ? "#1a1a1a" : "#e9ecef",
    borderRadius: "12px",
    padding: "12px",
    cursor: cropMode ? "default" : "pointer",
  };

  const imgStyle = {
    maxWidth: "100%",
    maxHeight: "min(52vh, 400px)",
    width: "auto",
    height: "auto",
    display: "block",
  };

  const modalTree = (
    <CustomModal
      isOpen
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      showButton
      onSubmit={handleApply}
      submitButtonText={applying ? "Selecting…" : confirmButtonText}
      submitButtonDisabled={!canApply || applying}
      modalSize="modal-lg"
      minHeight="420px"
      maxHeight="90vh"
      isCentered
      zIndexBase={zIndexBase}
    >
      <div
        className="d-flex flex-column align-items-center pb-2"
        style={{
          ...(isDarkMode
            ? {
                ["--ReactCrop-border-color"]: "rgba(255,255,255,0.35)",
                ["--ReactCrop-cropper-outline-color"]: "rgba(0,0,0,0.55)",
                ["--ReactCrop-handle-bg"]: "#fff",
                ["--ReactCrop-handle-border"]: "1px solid rgba(0,0,0,0.35)",
              }
            : {}),
        }}
      >
        {!cropMode && (
          <p
            className="mb-2 text-center"
            style={{
              fontSize: "0.8125rem",
              color: isDarkMode ? "#adb5bd" : "#6c757d",
            }}
          >
            Click the image to crop, or press <strong>Select image</strong> to use it as-is.
          </p>
        )}
        <div
          className="w-100 d-flex justify-content-center align-items-start"
          style={previewBoxStyle}
          onClick={!cropMode ? enableCropMode : undefined}
          onKeyDown={
            !cropMode
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    enableCropMode();
                  }
                }
              : undefined
          }
          role={!cropMode ? "button" : undefined}
          tabIndex={!cropMode ? 0 : undefined}
          aria-label={!cropMode ? "Click to crop image" : undefined}
        >
          {imageSrc ? (
            cropMode ? (
              <ReactCrop
                crop={crop}
                onChange={(_, c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect && aspect > 0 ? aspect : undefined}
                ruleOfThirds
                className="mx-auto"
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop preview"
                  crossOrigin={
                    typeof imageSrc === "string" && /^https?:\/\//i.test(imageSrc)
                      ? "anonymous"
                      : undefined
                  }
                  onLoad={onImageLoad}
                  style={imgStyle}
                />
              </ReactCrop>
            ) : (
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Preview — click to crop"
                style={imgStyle}
              />
            )
          ) : null}
        </div>
      </div>
    </CustomModal>
  );

  if (typeof document === "undefined") return null;

  return createPortal(modalTree, document.body);
};

export default ImageCropModal;
